import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_IMAGE = "https://images.unsplash.com/photo-1568252542512-9fe8fe9c87bb?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";
const CATS = [
  { slug: "vetements", name: "Vêtements", img: "https://images.unsplash.com/photo-1664076458686-3449062080ac?crop=entropy&cs=srgb&fm=jpg&w=700&q=85" },
  { slug: "bijoux", name: "Bijoux", img: "https://images.unsplash.com/photo-1633934542430-0905ccb5f050?crop=entropy&cs=srgb&fm=jpg&w=700&q=85" },
  { slug: "decoration", name: "Décoration", img: "https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?crop=entropy&cs=srgb&fm=jpg&w=700&q=85" },
  { slug: "cosmetiques", name: "Cosmétiques & Beauté", img: "https://images.unsplash.com/photo-1631390179406-0bfe17e9f89d?crop=entropy&cs=srgb&fm=jpg&w=700&q=85" },
];

function Section({ eyebrow, title, link, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 md:py-24">
      <div className="flex items-end justify-between mb-10">
        <div>
          {eyebrow && <span className="over text-[0.62rem] text-[#D4AF37]">{eyebrow}</span>}
          <h2 className="font-display text-3xl lg:text-4xl mt-2">{title}</h2>
        </div>
        {link && <Link to={link} className="over text-[0.65rem] link-underline hidden sm:inline-flex items-center gap-2">Tout voir <ArrowRight size={14} /></Link>}
      </div>
      {children}
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [creators, setCreators] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then(({ data }) => setFeatured(data.products));
    api.get("/products", { params: { sort: "new", limit: 4 } }).then(({ data }) => setNewest(data.products));
    api.get("/creators", { params: { featured: true } }).then(({ data }) => setCreators(data.creators));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[82vh] min-h-[540px] overflow-hidden bg-[#0A0A0A]">
          <img src={HERO_IMAGE} alt="Cagole Chic" className="absolute inset-0 w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex flex-col justify-end pb-20">
            <span className="over text-[0.65rem] text-[#D4AF37] rise">Galerie éditée de créateurs indépendants</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white font-light leading-[1.05] mt-4 max-w-2xl rise" style={{ animationDelay: "120ms" }}>
              L'art de la création indépendante
            </h1>
            <div className="flex flex-wrap gap-4 mt-8 rise" style={{ animationDelay: "240ms" }}>
              <Link to="/catalogue" className="btn btn-magenta" data-testid="hero-discover-button">Découvrir la galerie</Link>
              <Link to="/createurs" className="btn btn-outline border-white text-white bg-black/20 backdrop-blur-sm hover:bg-white hover:text-black">Nos créateurs</Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#0A0A0A] text-white overflow-hidden py-4 border-y border-white/10">
        <div className="marquee whitespace-nowrap flex">
          {[0, 1].map((k) => (
            <span key={k} className="over text-[0.62rem] text-neutral-400 mx-6">
              Sélection éditoriale • Pièces d'exception • Pièces uniques • Luxe contemporain • Créateurs indépendants • Sélection éditoriale • Pièces d'exception • Pièces uniques • Luxe contemporain • Créateurs indépendants •
            </span>
          ))}
        </div>
      </div>

      {/* CATEGORIES */}
      <Section eyebrow="Explorer" title="Les univers">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {CATS.map((c, i) => (
            <Link key={c.slug} to={`/c/${c.slug}`} className="group relative overflow-hidden rise" style={{ animationDelay: `${i * 80}ms` }} data-testid={`home-cat-${c.slug}`}>
              <div className="aspect-[4/5] bg-neutral-100 overflow-hidden">
                <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <span className="absolute bottom-5 left-5 font-display text-2xl text-white">{c.name}</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* COUPS DE COEUR */}
      <div className="bg-[#FAFAFA]">
        <Section eyebrow="Sélection de la semaine" title="Coups de cœur" link="/catalogue?sort=selection">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-8">
            {featured.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      </div>

      {/* FEATURED CREATORS */}
      <Section eyebrow="Rencontre avec nos artisans" title="Nos créateurs" link="/createurs">
        <div className="grid md:grid-cols-3 gap-8">
          {creators.slice(0, 3).map((c, i) => (
            <Link key={c.id} to={`/createur/${c.id}`} className="group rise" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="aspect-square overflow-hidden bg-[#0A0A0A]">
                {c.portrait_url ? <img src={c.portrait_url} alt={c.brand_name} className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
                  : <div className="w-full h-full flex items-center justify-center"><span className="font-display text-3xl text-white">{c.brand_name}</span></div>}
              </div>
              <span className="over text-[0.6rem] text-[#D4AF37] mt-4 block">{c.main_category}</span>
              <h3 className="font-display text-2xl mt-1">{c.brand_name}</h3>
              <p className="text-sm text-neutral-500 mt-2 leading-relaxed line-clamp-2">{c.universe}</p>
              <span className="over text-[0.62rem] mt-3 inline-flex items-center gap-2 link-underline">Découvrir son univers <ArrowRight size={13} /></span>
            </Link>
          ))}
        </div>
      </Section>

      {/* EDITORIAL BANNER */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="over text-[0.62rem] text-[#D4AF37]">Notre philosophie</span>
            <h2 className="font-display text-4xl lg:text-5xl font-light mt-4 leading-tight">Une maison, une infinité de talents</h2>
            <p className="text-neutral-300 mt-6 leading-relaxed max-w-lg">Chaque pièce est choisie à la main, chaque créateur rigoureusement sélectionné. Cagole Chic réunit l'excellence des ateliers indépendants au sein d'une seule enseigne, pour une expérience d'achat aussi fluide qu'un grand magasin de luxe.</p>
            <div className="hairline w-24 my-8" />
            <div className="grid grid-cols-2 gap-8">
              {[["Créations", "sélectionnées"], ["Créateurs", "indépendants"], ["Pièces", "originales"], ["Paiement", "sécurisé"]].map(([a, b]) => (
                <div key={a}><p className="font-display text-xl">{a}</p><p className="over text-[0.58rem] text-neutral-400 mt-1">{b}</p></div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {newest.slice(0, 4).map((p) => (
              <Link key={p.id} to={`/produit/${p.id}`} className="aspect-[3/4] overflow-hidden">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BECOME CREATOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24">
        <div className="border border-[#D4AF37]/40 px-8 lg:px-20 py-16 text-center">
          <span className="over text-[0.62rem] text-[#D4AF37]">Rejoignez la sélection</span>
          <h2 className="font-display text-4xl lg:text-5xl font-light mt-4">Vous êtes créateur ?</h2>
          <p className="text-neutral-500 mt-4 max-w-xl mx-auto">Présentez votre univers à une communauté exigeante. Candidature étudiée avec soin par notre comité de sélection.</p>
          <Link to="/devenir-createur" className="btn btn-dark mt-8" data-testid="home-become-creator-button">Devenir créateur</Link>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#FAFAFA]">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <span className="over text-[0.62rem] text-[#D4AF37]">Le Journal Cagole Chic</span>
          <h2 className="font-display text-3xl lg:text-4xl mt-3">Recevez nos sélections</h2>
          <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-0 mt-8 border-b border-neutral-900">
            <input type="email" required placeholder="Votre adresse e-mail" className="flex-1 bg-transparent outline-none py-3 text-sm" data-testid="newsletter-input" />
            <button className="over text-[0.62rem] px-4">S'inscrire</button>
          </form>
        </div>
      </section>
    </div>
  );
}
